/**
 * Repairs report share links whose `?accessToken=` separator was percent-encoded
 * before the link reached the browser.
 *
 * Mail clients, ticketing tools and webhook consumers routinely re-encode a URL
 * they pass along. Each hop turns `?` into `%3F` and every following hop turns
 * the `%` into `%25`, so a link forwarded a few times arrives looking like
 *
 *   /reports/<uuid>%25252525253FaccessToken%25252525253DeyJ...SnI/email-template
 *
 * Angular then matches the whole blob as the `:id` route param, components send
 * it to the API verbatim, and Django answers 404 because it is not a UUID.
 */

/** Percent-encoding survives a bounded number of forwarding hops in practice. */
const MAX_DECODE_PASSES = 12;

/** An encoded '%' or an encoded query delimiter means another pass is needed. */
const NEEDS_DECODING = /%25|%3F|%3D|%26/i;

export interface NormalizedReportUrl {
  /** Canonical URL with a real query string; safe to hand to Router.parseUrl. */
  url: string;
  /** The `accessToken` value, or null when the link carries none. */
  token: string | null;
  /** True when the incoming URL was encoded/glued and must be redirected. */
  wasMangled: boolean;
}

function fullyDecode(url: string): string {
  let current = url;
  for (let pass = 0; pass < MAX_DECODE_PASSES; pass++) {
    if (!NEEDS_DECODING.test(current)) break;
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      // Malformed escape sequence: keep what we have rather than losing the URL.
      break;
    }
    if (next === current) break;
    current = next;
  }
  return current;
}

export function normalizeReportUrl(rawUrl: string): NormalizedReportUrl {
  // Encoding the delimiters never touches the parameter name, so its absence
  // means this is not a share link. Leaving those URLs alone keeps us from
  // rewriting query values that are legitimately encoded (e.g. '%3D' padding).
  if (!rawUrl.includes('accessToken')) {
    return { url: rawUrl, token: null, wasMangled: false };
  }

  const decoded = fullyDecode(rawUrl);
  const separator = decoded.indexOf('?');

  if (separator === -1) {
    return { url: rawUrl, token: null, wasMangled: false };
  }

  // Quotes are never valid in these routes; they arrive when a link is built
  // from a JSON value (/documents/"<uuid>") and would reach the API as %22.
  let path = decoded.slice(0, separator).replace(/["']/g, '');
  const quotesStripped = path !== decoded.slice(0, separator);
  // Additional '?' can surface once the encoded ones are decoded; only the
  // first separates path from query, the rest are really parameter joiners.
  const params = new URLSearchParams(decoded.slice(separator + 1).replace(/\?/g, '&'));

  let token = params.get('accessToken');
  let tokenSwallowedPath = false;

  if (token) {
    // A JWT is base64url (A-Z a-z 0-9 - _ .) so it can never contain '/'.
    // Anything past a '/' is route path that got swallowed when the '?' was
    // encoded, e.g. token value "eyJ...SnI/email-template".
    const slash = token.indexOf('/');
    if (slash !== -1) {
      const tail = token.slice(slash + 1).replace(/^\/+/, '');
      path = `${path.replace(/\/+$/, '')}/${tail}`;
      token = token.slice(0, slash);
      tokenSwallowedPath = true;
    }
    params.set('accessToken', token);
  }

  const query = params.toString();
  const url = query ? `${path}?${query}` : path;

  return {
    url,
    token: token || null,
    wasMangled: tokenSwallowedPath || quotesStripped || decoded !== rawUrl,
  };
}

/** A PRA report id is always a UUID; the API's URLconf accepts nothing else. */
export const REPORT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Percent-encoded octet, used to detect an id that needs another decode pass. */
const ENCODED_OCTET = /%[0-9a-f]{2}/i;

/**
 * Recovers a usable report UUID from a route param that picked up junk.
 *
 * Three kinds of debris show up in practice:
 *  - JSON quotes, from a link pasted straight out of an API response
 *    (`/documents/"1ca40e84-..."`), which reach the API as `%22`;
 *  - stacked percent-encoding, when an id read back from a serialized URL is
 *    fed into a new link and re-encoded on every navigation;
 *  - a glued-on `?accessToken=...`, from a share link whose separator was
 *    encoded before it reached the browser.
 *
 * Returns null when nothing UUID-shaped survives, so callers can refuse to
 * build a request instead of hammering the API with a 404 per poll.
 */
export function sanitizeReportId(raw: string | null | undefined): string | null {
  if (!raw) return null;

  let value = String(raw);
  for (let pass = 0; pass < MAX_DECODE_PASSES && ENCODED_OCTET.test(value); pass++) {
    let next: string;
    try {
      next = decodeURIComponent(value);
    } catch {
      break;
    }
    if (next === value) break;
    value = next;
  }

  // Anything from a delimiter onwards belongs to the URL, not to the id.
  value = value.split(/[?#&/]/)[0];
  // Quotes and whitespace are never part of a UUID.
  value = value.replace(/["'\s]/g, '');

  return REPORT_ID_RE.test(value) ? value.toLowerCase() : null;
}

/** Decodes a JWT payload. Uses base64url, which plain `atob` rejects. */
export function decodeJwtPayload(token: string): Record<string, any> | null {
  const segment = token.split('.')[1];
  if (!segment) return null;
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  try {
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}
